//* 动画库
import { gsap } from "gsap";
//* 组件库
import type { UiTypes, UiForm } from "various-ui";
import { $loading, utility } from "various-ui";
//* 工具函数
import { Formats } from "@/assets/js";

//* 私有组件
import type ModuleTutorial from "../components/tutorial/index.vue";

//* 组件属性
import { useOptions } from "./options";

export const useComposable = () => {
    //* 插件
    const i18n = useI18n();
    const route = useRoute();
    //* 配置项
    const mode = (route.meta?.mode?.toString() || "gene") as "gene" | "model" | "disease";
    const options = reactive(useOptions(mode));

    //* 静态数据
    const vars = {
        rowspan_views_link: ["related_genes", "related_models", "related_diseases", "protein_interaction"],
        rowspan_views: ["disease_characterization", "homozygous_characterization", "heterozygous_characterization"],
        rowspan_nots: ["tissue", "cells"],
    };

    //* 响应式变量
    const refs = {
        checkbox: ref<boolean>(false), //* 高亮
        data: ref<any[]>([undefined, undefined, undefined, undefined, undefined]), //* 已添加基因信息
    };

    //* 节点
    const nodes = {
        tutorial: ref<InstanceType<typeof ModuleTutorial>>(),
        form: ref<InstanceType<typeof UiForm>>(),
    };

    //* 工具函数
    const utils = {
        verify: (index: number) => {
            const datas = refs.data.value.filter((value) => value);
            if (datas.find((value) => value.id == keywords[index].id)) return { verify: false, message: options.error };
            else {
                return { verify: true, message: "" };
            }
        },

        normalize: (data: any) => {
            if (!data) return "";
            if (data instanceof Date) return data.toISOString();
            if (utility.isArray(data) || utility.isObject(data)) return JSON.stringify(data);
            else {
                return String(data);
            }
        },

        recursion: (ids: string[], index: number) => {
            if (!ids.length) return;
            else if (!ids?.[index]) return methods.comparison();
            else {
                return methods.acquire(ids[index], index, (i) => utils.recursion(ids, i));
            }
        },
    };

    //* 关键词数据
    const keywords = reactive([
        { id: "", key: "search0", value: "" },
        { id: "", key: "search1", value: "" },
        { id: "", key: "search2", value: "" },
        { id: "", key: "search3", value: "" },
        { id: "", key: "search4", value: "" },
    ]);

    //* 表单校验规则
    const rules: { [name: string]: UiTypes.verifyRule[] } = {
        search0: [{ trigger: "verify", verify: () => utils.verify(0) }],
        search1: [{ trigger: "verify", verify: () => utils.verify(1) }],
        search2: [{ trigger: "verify", verify: () => utils.verify(2) }],
        search3: [{ trigger: "verify", verify: () => utils.verify(3) }],
        search4: [{ trigger: "verify", verify: () => utils.verify(4) }],
    };

    //* 函数列表
    const methods = {
        //* 页面初始化
        init: () => {
            //* 表单状态初始化
            nodes.form.value?.reset();
            //* 数据初始化
            refs.data.value = [undefined, undefined, undefined, undefined, undefined];
            //* 数据获取
            utils.recursion(Formats.querys(route.query.id), 0);
        },

        //* 清空搜索基因
        clear: (index: number) => {
            //* 数据更新
            keywords[index].id = "";
            keywords[index].value = "";
            refs.data.value[index] = undefined;
            //* 触发数据比对
            methods.comparison();
        },

        //* 选择基因
        select: (data: any, index: number) => {
            //* 数据更新
            keywords[index].id = data.id;
            keywords[index].value = data.value;
            //* 表单校验
            nextTick(() => {
                nodes.form.value?.validator((result) => result && methods.acquire(data.id, index), [`search${index}`]);
            });
        },

        //* 获取数据
        acquire: (id: string, index: number, callBack?: (number: number) => void) => {
            $loading.open({ message: i18n.t("UnderLoad") });
            api.frontend("/api/comparison", {
                data: { mode: mode, id: id },
                finally: () => $loading.close(),
                success: (response) => {
                    //* 数据更新
                    refs.data.value[index] = response;
                    //* 存在回调则不触发数据比对
                    if (callBack) callBack(index + 1);
                    else {
                        methods.comparison();
                    }
                },
            });
        },

        //* 比对数据
        comparison: () => {
            if (!refs.checkbox.value) return;
            else {
                const data = refs.data.value.filter((value) => value)?.[0] || {};
                const datas = refs.data.value.filter((value) => value).slice(1);
                if (datas.length < 1) options.option.forEach((opt) => opt.option.forEach((value) => (value.same = false)));
                else {
                    options.option.forEach((opt) => {
                        opt.option.forEach((value) => {
                            value.same = datas.every(
                                (v) => utils.normalize(v?.[opt.key]?.[value.key]) == utils.normalize(data?.[opt.key]?.[value.key])
                            );
                        });
                    });
                }
            }
        },
    };

    //* 响应事件
    const ons = {
        animations: {
            "before-enter": (el: Element) => gsap.set(el, { height: 0, opacity: 0 }),
            "enter": (el: Element, done: () => void) => gsap.to(el, { height: "auto", opacity: 1, duration: 0.2, onComplete: done }),
            "leave": (el: Element, done: () => void) => gsap.to(el, { height: 0, opacity: 0, duration: 0.2, onComplete: done }),
        },
    };

    return { ons, vars, refs, mode, nodes, rules, methods, options, keywords };
};
